import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import './ProspectFinderResults.css'; // Reuse table styles
import '../analysis/MarketAnalysisView.css'; // Reuse advanced section styles

export default function B2CRecommendationView({
  userId,
  refreshProfile,
  initialTask,
  hideNewAnalysis = false,
  hideSteps = false,
}) {
  const [productDescription, setProductDescription] = useState('');
  const [targetRegion, setTargetRegion] = useState('Global');
  const [loading, setLoading] = useState(!!initialTask);
  const [task, setTask] = useState(initialTask || null);
  const [errorMessage, setErrorMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const pollIntervalRef = useRef(null);

  const fetchHistory = async () => {
    try {
      const res = await axios.get('/b2c/history');
      if (res.data.success) setHistory(res.data.data);
    } catch (err) {
      console.error('History fetch failed:', err);
    }
  };

  useEffect(() => {
    fetchHistory();

    // Handle initialTask from Unified Search
    if (initialTask) {
      setTask(initialTask);
      setLoading(true);
      startPolling(initialTask.id);
    }

    return () => clearInterval(pollIntervalRef.current);
  }, [initialTask]);

  const startPolling = (id) => {
    if (!id || id === 'undefined') {
      console.warn('[B2CView] Skipping polling due to invalid task ID:', id);
      return;
    }
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/b2c/recommendations/${id}`);
        if (res.data.success) {
          const data = res.data.data;
          setTask(data);
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setLoading(false);
            if (data.status === 'failed') {
              setErrorMessage(data.error_message || 'Analysis failed. Please try again.');
            }
            if (typeof refreshProfile === 'function') refreshProfile();
            fetchHistory();
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
        if (err.response?.status === 403 || err.response?.status === 404) {
          clearInterval(pollIntervalRef.current);
          setLoading(false);
        }
      }
    }, 3000);
  };

  const entryAnimationStyle = `
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    `;

  const renderPersona = (persona) => (
    <div style={{ padding: '2px 0 8px', fontFamily: 'inherit', animation: 'fadeIn 0.4s ease' }}>
      <style>{entryAnimationStyle}</style>

      {/* Two-col grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '10px',
          marginBottom: '10px',
        }}
      >
        {/* Demographics block */}
        <div
          style={{
            border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
            borderRadius: '12px',
            padding: '14px 16px',
            background: 'var(--color-background-primary, #ffffff)',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'var(--color-text-tertiary, #94a3b8)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: '10px',
            }}
          >
            Demographics
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {[
              { key: 'Age range', val: persona.demographics?.ageRange },
              { key: 'Occupation', val: persona.demographics?.occupation },
              { key: 'Net worth', val: persona.demographics?.incomeLevel },
            ].map(
              (row, i) =>
                row.val && (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: '8px',
                      padding: '8px 0',
                      borderBottom: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-text-secondary, #64748b)',
                        flexShrink: 0,
                      }}
                    >
                      {row.key}
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: 'var(--color-text-primary, #0f172a)',
                        textAlign: 'right',
                      }}
                    >
                      {row.val}
                    </span>
                  </div>
                )
            )}
            {persona.demographics?.typicalLocations?.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '6px',
                  padding: '8px 0',
                }}
              >
                <span style={{ fontSize: '12px', color: 'var(--color-text-secondary, #64748b)' }}>
                  Locations
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {persona.demographics.typicalLocations.map((loc, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: '11px',
                        color: 'var(--color-text-secondary, #64748b)',
                        background: 'var(--color-background-secondary, #f8fafc)',
                        padding: '2px 8px',
                        borderRadius: '100px',
                      }}
                    >
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lifestyle + Interests stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div
            style={{
              border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
              borderRadius: '12px',
              padding: '14px 16px',
              background: 'var(--color-background-primary, #ffffff)',
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--color-text-tertiary, #94a3b8)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '8px',
              }}
            >
              Lifestyle
            </div>
            <div
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary, #64748b)',
                lineHeight: '1.6',
              }}
            >
              {persona.psychographics?.lifestyle || '—'}
            </div>
          </div>
          <div
            style={{
              border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
              borderRadius: '12px',
              padding: '14px 16px',
              background: 'var(--color-background-primary, #ffffff)',
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--color-text-tertiary, #94a3b8)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '8px',
              }}
            >
              Interests
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {(persona.psychographics?.interests || persona.interests || []).map((it, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    padding: '4px 10px',
                    borderRadius: '100px',
                    background: '#FAEEDA',
                    color: '#854F0B',
                  }}
                >
                  {it}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pain points */}
      {persona.painPoints?.length > 0 && (
        <div
          style={{
            border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
            borderRadius: '12px',
            padding: '14px 16px',
            background: 'var(--color-background-primary, #ffffff)',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'var(--color-text-tertiary, #94a3b8)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: '10px',
            }}
          >
            Pain points
          </div>
          <div>
            {persona.painPoints.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom:
                    i < persona.painPoints.length - 1
                      ? '0.5px solid var(--color-border-tertiary, #e2e8f0)'
                      : 'none',
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: 'var(--color-text-tertiary, #94a3b8)',
                    minWidth: '18px',
                    paddingTop: '1px',
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-text-secondary, #64748b)',
                    lineHeight: '1.5',
                  }}
                >
                  {p}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderRecommendations = (recs) => (
    <div style={{ marginTop: '4px' }}>
      <div
        style={{
          height: '0.5px',
          background: 'var(--color-border-tertiary, #e2e8f0)',
          margin: '16px 0',
        }}
      ></div>
      <div
        style={{
          fontSize: '11px',
          fontWeight: '500',
          color: 'var(--color-text-tertiary, #94a3b8)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: '10px',
        }}
      >
        Recommended channels
      </div>
      <div
        style={{
          border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {recs.map((rec, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 14px',
              background: 'var(--color-background-primary, #ffffff)',
              borderBottom:
                i < recs.length - 1 ? '0.5px solid var(--color-border-tertiary, #e2e8f0)' : 'none',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'var(--color-background-secondary, #f8fafc)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'var(--color-background-primary, #ffffff)')
            }
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background:
                  rec.type === 'Individual'
                    ? 'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)'
                    : 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)',
                border: rec.type === 'Individual' ? '1px solid #bfdbfe' : '1px solid #fed7aa',
              }}
            >
              <i
                className={rec.type === 'Individual' ? 'fas fa-user' : 'fas fa-users'}
                style={{
                  fontSize: '14px',
                  color: rec.type === 'Individual' ? '#2563eb' : '#c2410c',
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary, #0f172a)',
                }}
              >
                {rec.name}
                <span
                  style={{
                    fontSize: '10px',
                    padding: '2px 7px',
                    borderRadius: '100px',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background: rec.type === 'Individual' ? '#E6F1FB' : '#FAECE7',
                    color: rec.type === 'Individual' ? '#185FA5' : '#993C1D',
                  }}
                >
                  {rec.type}
                </span>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary, #64748b)',
                  marginTop: '2px',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {rec.reason}
              </div>
            </div>

            {rec.url ? (
              <a
                href={rec.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-info, #185FA5)',
                  textDecoration: 'none',
                  fontWeight: '500',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                →
              </a>
            ) : (
              <span
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary, #94a3b8)',
                  flexShrink: 0,
                }}
              >
                No link
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (errorMessage) {
    return (
      <div
        style={{
          padding: '16px 18px',
          marginBottom: '20px',
          color: '#991b1b',
          background: '#fef2f2',
          borderRadius: '16px',
          border: '1px solid #fecaca',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <i className="fas fa-exclamation-circle fa-lg"></i>
        <div style={{ fontSize: '14px', fontWeight: '600' }}>{errorMessage}</div>
      </div>
    );
  }

  if (loading && task && task.status === 'processing' && !hideSteps) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center', opacity: 0.5 }}>
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p style={{ marginTop: '10px' }}>Processing analysis...</p>
      </div>
    );
  }

  if (task && task.status === 'completed') {
    return (
      <div
        className="b2c-results-view"
        style={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          padding: '16px 0 0 0',
        }}
      >
        <style>
          {`
                        .b2c-results-view::-webkit-scrollbar { width: 5px; }
                        .b2c-results-view::-webkit-scrollbar-track { background: transparent; }
                        .b2c-results-view::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
                        .b2c-results-view::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
                    `}
        </style>
        {task.ideal_persona && renderPersona(task.ideal_persona)}
        {task.recommendations && renderRecommendations(task.recommendations)}

        {!hideNewAnalysis && (
          <button
            onClick={() => {
              setTask(null);
              setProductDescription('');
            }}
            style={{
              marginTop: '32px',
              background: 'none',
              border: '1px solid #e2e8f0',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-undo"></i> Start New Analysis
          </button>
        )}
      </div>
    );
  }

  if (history.length > 0 && !loading && !task && !hideNewAnalysis) {
    return (
      <div style={{ marginTop: '48px' }}>
        <h3
          style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}
        >
          Past Recommendations
        </h3>
        <div
          className="history-list"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {history.map((h) => (
            <div
              key={h.id}
              onClick={() => startPolling(h.id)}
              style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div
                style={{
                  fontWeight: '700',
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#1e293b',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  height: '40px',
                  lineHeight: '20px',
                }}
              >
                {h.product_description}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                <i className="fas fa-map-marker-alt"></i> {h.target_region}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                {new Date(h.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
